import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useClients = (user, token, showMessage) => {
  const [clients, setClients] = useState([]);
  const [clientStats, setClientStats] = useState({});
  const [showInactiveClients, setShowInactiveClients] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Client forms
  const [clientForm, setClientForm] = useState({
    name: '',
    code: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    admin_username: 'admin',
    admin_password: '',
    admin_email: '',
    user_limit: 15,
    asset_limit: 100,
    plan_name: 'Professional Plan',
    plan_price: '₹999/mo',
    plan_billing_cycle: 'monthly',
    billing_type: 'trial', // 'trial' | 'prepaid' | 'free'
    trial_days: 14,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    webhook_enabled: false
  });

  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    user_limit: 15,
    asset_limit: 100,
    plan_name: 'Professional Plan',
    plan_price: '₹999/mo',
    plan_billing_cycle: 'monthly',
    plan_status: 'active',
    billing_type: 'prepaid',
    trial_days: 14,
    start_date: '',
    end_date: '',
    webhook_enabled: false
  });

  const [editingUserLimit, setEditingUserLimit] = useState(null);
  const [userLimitForm, setUserLimitForm] = useState({ user_limit: 10 });
  const [editingAssetLimit, setEditingAssetLimit] = useState(null);
  const [assetLimitForm, setAssetLimitForm] = useState({ asset_limit: 50 });

  // Fetch all clients (superadmin only)
  const fetchClients = useCallback(async () => {
    if (!token || user?.role !== 'superadmin') return;
    try {
      const response = await apiClient(`/api/clients/?include_inactive=${showInactiveClients}`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);

        // Fetch client statistics
        const statsRes = await apiClient('/api/clients/stats/summary');
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setClientStats(stats);
        }
      }
    } catch (error) {
      console.error('Fetch clients error:', error);
    }
  }, [token, user, showInactiveClients]);

  // Create Client
  const handleCreateClient = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingClients(true);

    try {
      const response = await apiClient('/api/clients/', {
        method: 'POST',
        body: clientForm,
      });

      if (response.ok) {
        await fetchClients();
        setClientForm({
          name: '',
          code: '',
          contact_email: '',
          contact_phone: '',
          address: '',
          admin_username: 'admin',
          admin_password: '',
          admin_email: '',
          user_limit: 15,
          asset_limit: 100,
          plan_name: 'Professional Plan',
          plan_price: '₹999/mo',
          plan_billing_cycle: 'monthly',
          billing_type: 'trial',
          trial_days: 14,
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          webhook_enabled: false
        });
        if (showMessage) showMessage('Client organization created successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to create client organization', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error creating client', 'error');
      return false;
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Edit Client
  const handleEditClient = async (e) => {
    if (e) e.preventDefault();
    if (!editingClient) return;

    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${editingClient.id}`, {
        method: 'PUT',
        body: editClientForm,
      });

      if (response.ok) {
        await fetchClients();
        setEditingClient(null);
        if (showMessage) showMessage('Client updated successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update client', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating client', 'error');
      return false;
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Export Full Client Data Archive (.JSON)
  const handleExportClientData = async (clientId, clientCode = 'TENANT') => {
    try {
      if (showMessage) showMessage('Generating tenant data archive download...', 'info');
      const response = await apiClient(`/api/clients/${clientId}/export-data`);
      if (response.ok) {
        const data = await response.json();
        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, 2))}`;
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute('href', jsonString);
        downloadAnchor.setAttribute('download', `NEXDESK_BACKUP_${clientCode.toUpperCase()}_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
        if (showMessage) showMessage('Tenant data archive downloaded successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to export tenant data', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error downloading tenant data archive', 'error');
      return false;
    }
  };

  // 24-Hour Soft Delete Client with Reason
  const handleSoftDeleteClient = async (clientId, reason = 'Administrative Decommissioning', notes = '') => {
    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${clientId}`, {
        method: 'DELETE',
        body: { reason, notes }
      });

      if (response.ok) {
        await fetchClients();
        if (showMessage) showMessage('Tenant moved to 24-Hour Deletion Grace Period. You can reactivate anytime within 24 hours.', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to initiate tenant deletion', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error initiating tenant deletion', 'error');
      return false;
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleDeleteClient = handleSoftDeleteClient;

  // Permanently Delete Client (Immediate Purge)
  const handlePermanentlyDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`⚠️ PERMANENT WIPE: Are you sure you want to completely erase "${clientName}" and ALL related database records immediately? This CANNOT be undone.`)) {
      return;
    }

    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${clientId}/permanent`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchClients();
        if (showMessage) showMessage(`Client "${clientName}" and all associated data permanently erased.`, 'info');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to permanently delete client', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error permanently purging client', 'error');
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Reactivate Client
  const handleReactivateClient = async (clientId) => {
    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${clientId}/reactivate`, {
        method: 'PUT',
      });

      if (response.ok) {
        await fetchClients();
        if (showMessage) showMessage('Tenant organization reactivated successfully! All operations restored.', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to reactivate client', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error reactivating client', 'error');
      return false;
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Update User Limit
  const handleUpdateUserLimit = async (clientId, newLimit) => {
    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${clientId}/user-limit`, {
        method: 'PUT',
        body: { user_limit: newLimit },
      });

      if (response.ok) {
        await fetchClients();
        setEditingUserLimit(null);
        if (showMessage) showMessage('User quota limit updated!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update user limit', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating user limit', 'error');
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Update Asset Limit
  const handleUpdateAssetLimit = async (clientId, newLimit) => {
    setIsLoadingClients(true);
    try {
      const response = await apiClient(`/api/clients/${clientId}/asset-limit`, {
        method: 'PUT',
        body: { asset_limit: newLimit },
      });

      if (response.ok) {
        await fetchClients();
        setEditingAssetLimit(null);
        if (showMessage) showMessage('Asset quota limit updated!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update asset limit', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating asset limit', 'error');
    } finally {
      setIsLoadingClients(false);
    }
  };

  return {
    clients,
    clientStats,
    showInactiveClients,
    setShowInactiveClients,
    isLoadingClients,
    clientForm,
    setClientForm,
    editingClient,
    setEditingClient,
    editClientForm,
    setEditClientForm,
    editingUserLimit,
    setEditingUserLimit,
    userLimitForm,
    setUserLimitForm,
    editingAssetLimit,
    setEditingAssetLimit,
    assetLimitForm,
    setAssetLimitForm,
    fetchClients,
    handleCreateClient,
    handleEditClient,
    handleDeleteClient,
    handleSoftDeleteClient,
    handleExportClientData,
    handlePermanentlyDeleteClient,
    handleReactivateClient,
    handleUpdateUserLimit,
    handleUpdateAssetLimit,
  };
};
